<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { API, HOST_TULUM } from '@/utils/http'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  }
})

const store = useStore()
const route = useRoute()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => `layer-${props.layer?.uuid}`)

const isLoading = ref(true)
const isInitialized = ref(false)
const mosaics = ref({ monthly: [], quarterly: [] })
const activeTab = ref('monthly')

// Monthly state
const monthNames = [
  'January', 'February', 'March',
  'April', 'May', 'June',
  'July', 'August', 'September',
  'October', 'November', 'December'
]
const monthlyAvailableMonthsByYear = ref({})
const monthlyYearOptions = ref([])
const monthlySelectedYear = ref(null)
const monthlySelectedMonth = ref(null)

// Quarterly state
const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4']
const quarterlyAvailableQuartersByYear = ref({})
const quarterlyYearOptions = ref([])
const quarterlySelectedYear = ref(null)
const quarterlySelectedQuarter = ref(null)

const monthOptionsForSelectedYear = computed(() => {
  if (!monthlySelectedYear.value) return []
  const monthsSet = monthlyAvailableMonthsByYear.value[Number(monthlySelectedYear.value)]
  if (!monthsSet) return []
  return [...monthsSet].sort((a, b) => a - b).map(m => String(m))
})

const quarterOptionsForSelectedYear = computed(() => {
  if (!quarterlySelectedYear.value) return []
  const quartersSet = quarterlyAvailableQuartersByYear.value[Number(quarterlySelectedYear.value)]
  if (!quartersSet) return []
  return [...quartersSet].sort((a, b) => a - b).map(q => String(q))
})

const initMonthlyData = () => {
  mosaics.value.monthly.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const mo = date.getUTCMonth() + 1
    if (!monthlyAvailableMonthsByYear.value[y]) {
      monthlyAvailableMonthsByYear.value[y] = new Set()
    }
    monthlyAvailableMonthsByYear.value[y].add(mo)
  })

  const numericYears = Object.keys(monthlyAvailableMonthsByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)
  monthlyYearOptions.value = numericYears.map(y => String(y))

  if (mosaics.value.monthly.length) {
    const stored = store.getters['layers/getPlanetscopeSelection'](layerId.value)
    if (stored && stored.tab === 'monthly' && stored.year && stored.month) {
      monthlySelectedYear.value = stored.year
      monthlySelectedMonth.value = stored.month
    } else {
      const defaultMosaic = mosaics.value.monthly[0]
      const defaultDate = new Date(defaultMosaic.first_acquired)
      monthlySelectedYear.value = String(defaultDate.getUTCFullYear())
      monthlySelectedMonth.value = String(defaultDate.getUTCMonth() + 1)
    }
  }
}

const initQuarterlyData = () => {
  mosaics.value.quarterly.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const q = Math.floor(date.getUTCMonth() / 3) + 1
    if (!quarterlyAvailableQuartersByYear.value[y]) {
      quarterlyAvailableQuartersByYear.value[y] = new Set()
    }
    quarterlyAvailableQuartersByYear.value[y].add(q)
  })

  const numericYears = Object.keys(quarterlyAvailableQuartersByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)
  quarterlyYearOptions.value = numericYears.map(y => String(y))

  if (mosaics.value.quarterly.length) {
    const defaultMosaic = mosaics.value.quarterly[0]
    const defaultDate = new Date(defaultMosaic.first_acquired)
    quarterlySelectedYear.value = String(defaultDate.getUTCFullYear())
    quarterlySelectedQuarter.value = String(Math.floor(defaultDate.getUTCMonth() / 3) + 1)
  }
}

// Dispatch selection changes to store so sidebar variant can react
const dispatchSelection = () => {
  if (!layerId.value) return
  if (activeTab.value === 'monthly') {
    store.dispatch('layers/setPlanetscopeSelection', {
      layerId: layerId.value,
      selection: {
        tab: 'monthly',
        year: monthlySelectedYear.value,
        month: monthlySelectedMonth.value
      }
    })
  }
}

watch([monthlySelectedYear, monthlySelectedMonth], () => {
  if (!isInitialized.value) return
  if (activeTab.value === 'monthly') {
    dispatchSelection()
  }
})

watch([quarterlySelectedYear, quarterlySelectedQuarter], () => {
  // Quarterly selection can be dispatched if needed in the future
})

watch(activeTab, () => {
  if (!isInitialized.value) return
  dispatchSelection()
})

onMounted(async () => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST_TULUM}/tiles/planetscope/${orgId.value}`
    })

    if (!response.data) return
    mosaics.value = response.data

    if (mosaics.value.monthly.length === 0 && mosaics.value.quarterly.length === 0) {
      return
    }

    mosaics.value.monthly.reverse()
    mosaics.value.quarterly.reverse()

    initMonthlyData()
    initQuarterlyData()
    isInitialized.value = true
    dispatchSelection()
  } catch (error) {
    console.error('Failed to load PlanetScope mosaics:', error)
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex items-center gap-2 py-4">
      <Spinner class="w-4 h-4" />
      <span class="text-sm text-muted-foreground">Loading mosaics...</span>
    </div>

    <div v-else-if="mosaics.monthly.length || mosaics.quarterly.length">
      <Tabs v-model="activeTab" class="w-full">
        <TabsList class="grid w-full grid-cols-2">
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly">
          <div v-if="mosaics.monthly.length" class="space-y-2 mt-2">
            <div class="flex space-x-2">
              <Select
                v-if="monthlyYearOptions.length"
                v-model="monthlySelectedYear"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="year in monthlyYearOptions"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                v-if="monthOptionsForSelectedYear.length"
                v-model="monthlySelectedMonth"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="month in monthOptionsForSelectedYear"
                      :key="month"
                      :value="month"
                    >
                      {{ monthNames[Number(month) - 1] }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quarterly">
          <div v-if="mosaics.quarterly.length" class="space-y-2 mt-2">
            <div class="flex space-x-2">
              <Select
                v-if="quarterlyYearOptions.length"
                v-model="quarterlySelectedYear"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="year in quarterlyYearOptions"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                v-if="quarterOptionsForSelectedYear.length"
                v-model="quarterlySelectedQuarter"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Quarter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem
                      v-for="q in quarterOptionsForSelectedYear"
                      :key="q"
                      :value="q"
                    >
                      {{ quarterNames[Number(q) - 1] }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>

    <p v-else class="text-sm text-muted-foreground py-4">No PlanetScope mosaics available</p>
  </div>
</template>
